/**
 * StripeActionModal — Extracted from ImmeublePortal.jsx (Batch #12 refactor).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { X, FileSignature, Banknote } from "lucide-react";
import { API, ModalStyles, StripeCheckoutPanel, StripeModeBanner, StripePADPanel, TabBtn, authH } from "./shared";

export function StripeActionModal({
  apartments, selectedApt, onClose, onReload,
}) {
  const [tab, setTab] = useState("pad");
  const [stripeMode, setStripeMode] = useState("live");
  const [stripeConfigured, setStripeConfigured] = useState(true);

  const tenants = (apartments || []).flatMap((a) =>
    (a.unit_tenants || []).map((t) => ({ ...t, _apt: a })),
  );
  const defaultTenantId = (selectedApt?.unit_tenants?.[0]?.id) || tenants[0]?.id || "";
  const [tenantId, setTenantId] = useState(defaultTenantId);
  const selectedTenant = tenants.find((t) => t.id === tenantId) || null;
  const selectedTenantApt = selectedTenant?._apt || null;

  // Batch #14.164 — Sync avec le sélecteur de logement en haut de page :
  // si l'utilisateur change le logement, on sélectionne automatiquement le 1er
  // locataire de ce logement (ou aucun s'il n'y en a pas).
  useEffect(() => {
    if (!selectedApt?.id) return;
    const aptTenants = selectedApt.unit_tenants || [];
    const newTid = aptTenants[0]?.id || "";
    if (newTid && newTid !== tenantId) {
      setTenantId(newTid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApt?.id]);

  useEffect(() => {
    axios.get(`${API}/portal/stripe/status`, { headers: authH() })
      .then((r) => {
        setStripeMode(r.data?.mode || "live");
        setStripeConfigured(!!r.data?.configured);
      })
      .catch(() => setStripeConfigured(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6" data-testid="immeuble-stripe-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[94vh] flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gradient-to-r from-violet-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center shadow-sm">
              <Banknote className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-slate-800">Prélèvements Stripe</h3>
                <StripeModeBanner mode={stripeMode} />
              </div>
              <p className="text-[10px] text-slate-500">Encaissement loyers · compte central DG Comptabilité</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/60 rounded-lg">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {!stripeConfigured && (
          <div className="mx-5 mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-700">
            Stripe n'est pas encore configuré sur ce serveur. Contactez votre administrateur.
          </div>
        )}

        <div className="px-5 pt-4 pb-2 border-b bg-slate-50/60">
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            Locataire concerné
          </label>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="cc-input"
            data-testid="immeuble-stripe-tenant-select"
          >
            {tenants.length === 0 && <option value="">— Aucun locataire dans cet immeuble —</option>}
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                Apt {t._apt?.apt_number || "?"} — {t.first_name} {t.last_name}
                {t.rent_amount != null ? ` · ${t.rent_amount.toLocaleString("fr-CA")} $/mois` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="px-5 pt-3 flex gap-1 border-b">
          <TabBtn active={tab === "pad"} onClick={() => setTab("pad")} icon={Banknote} label="Prélèvement bancaire (PAD)" testId="immeuble-stripe-tab-pad" />
          <TabBtn active={tab === "card"} onClick={() => setTab("card")} icon={FileSignature} label="Lien de paiement par carte" testId="immeuble-stripe-tab-card" />
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!selectedTenant ? (
            <div className="text-center py-12 text-sm text-slate-500 italic">
              Sélectionnez un locataire dans la liste ci-dessus pour commencer.
            </div>
          ) : tab === "pad" ? (
            <StripePADPanel tenant={selectedTenant} apt={selectedTenantApt} onReload={onReload} />
          ) : (
            <StripeCheckoutPanel tenant={selectedTenant} apt={selectedTenantApt} onReload={onReload} />
          )}
        </div>

        <ModalStyles />
      </div>
    </div>
  );
}
